import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import useHooks from "./useHooks";
import dayjs from "dayjs";

// Create context
const ReportContext = createContext(null);

export function ReportContextProvider({ children }) {
  const { useReportRun, useReportLogs } = useHooks();
  const { data: reportRunData, refetch: refetchReportRun } = useReportRun();

  const [executionId, setExecutionId] = useState(null);
  const intervalRef = useRef(null);

  // Initialize report logs hook with execution ID
  const {
    loading: logsLoading,
    data: logsData,
    refetch: refetchLogsQuery,
    clearData: clearLogsData,
  } = useReportLogs(executionId || "");

  // Create a stable refetch function that doesn't change with each render
  const refetchReportLogs = useCallback(
    (newId) => {
      const idToUse = newId || executionId;
      // Only refetch if we're not already loading and have a valid ID
      if (idToUse && !logsLoading) {
        refetchLogsQuery(idToUse);
      }
    },
    [executionId, refetchLogsQuery, logsLoading]
  );

  // Create a stable clearLogs function
  const clearReportLogs = useCallback(() => {
    clearLogsData();
  }, [clearLogsData]);

  // Update executionId when report run data changes
  useEffect(() => {
    if (reportRunData?.run?.refId) {
      setExecutionId(reportRunData.run.refId);
    }
  }, [reportRunData]);

  // Set up auto-refresh for report data and logs when a report is running
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Auto refresh data when report is running (status 1)
    const isReportRunning = reportRunData?.run?.status === 1;

    if (isReportRunning && executionId) {
      // Set up interval to refresh report data and logs
      intervalRef.current = setInterval(() => {
        refetchReportRun();
        refetchReportLogs(executionId);
      }, 10000);
    }

    // Clean up on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    reportRunData?.run?.status,
    executionId,
    refetchReportRun,
    refetchReportLogs,
  ]);

  // Function to determine if report logs are available
  const isReportLogsAvailable = useCallback(() => {
    return (
      reportRunData?.status === 200 &&
      executionId !== undefined &&
      executionId !== ""
    );
  }, [reportRunData?.status, executionId]);

  // Get report download count
  const getReportCount = useCallback(() => {
    if (!reportRunData?.run?.downloadLinksHtml) return 0;

    const parser = new DOMParser();
    const doc = parser.parseFromString(
      reportRunData.run.downloadLinksHtml,
      "text/html"
    );
    return doc.querySelectorAll("a").length;
  }, [reportRunData?.run?.downloadLinksHtml]);

  // Function to determine if report downloads are available
  const isDownloaLinksAvailable = useCallback(() => {
    return (
      (reportRunData?.run?.status === 2 ||
        reportRunData?.run?.status === 4 ||
        (reportRunData?.run?.status === -1 && getReportCount() > 0)) && // done = 2 or warning = 4
      reportRunData?.run?.downloadLinksHtml !== "" &&
      reportRunData?.run?.downloadLinksHtml !== null &&
      reportRunData?.run?.endTimestamp &&
      dayjs
        .utc(reportRunData?.run?.endTimestamp)
        .isAfter(dayjs.utc().subtract(1, "hour"))
    );
  }, [reportRunData?.run, getReportCount]);

  // Value to be provided by the context
  const contextValue = {
    reportRunData,
    refetchReportRun,
    executionId,
    logsData,
    logsLoading,
    refetchReportLogs,
    clearReportLogs,
    isReportLogsAvailable,
    isDownloaLinksAvailable,
    getReportCount,
  };

  return (
    <ReportContext.Provider value={contextValue}>
      {children}
    </ReportContext.Provider>
  );
}

// Hook to use the report context
export function useReportContext() {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error(
      "useReportContext must be used within a ReportContextProvider"
    );
  }
  return context;
}
